import React, { FC, useEffect, useMemo, useRef } from 'react';
import { Chart, ChartConfiguration, FontSpec, registerables } from 'chart.js';

Chart.register(...registerables);

type BarChartProps = {
  canvasClasses?: string;
  canvasStyle?: React.CSSProperties;
  legendLabel?: string;
  labels: string[];
  data: number[];
  labelXAxis?: string;
  labelYAxis?: string;
  tickLabelFormatterY?: (value: number | string) => string;
  renderTooltip?: (context: any, tooltipElRef: React.RefObject<HTMLElement>) => any;
};

const JOURNEY_BEDROCK_BLACK = '#101010';
const JOURNEY_BEDROCK_DARK_GRAY = '#9C9C9C';
const JOURNEY_BEDROCK_GRAY_MEDIUM = '#E6E6E6';

const FONT_SPEC_PARAGRAPH: Partial<FontSpec> = {
  family: "'CircularXX', sans-serif",
  weight: '450',
  size: 12,
  // lineHeight: 18,
};

export const BarChart: FC<BarChartProps> = ({
  canvasClasses,
  canvasStyle,
  legendLabel,
  labels,
  data,
  labelXAxis,
  labelYAxis,
  renderTooltip,
  tickLabelFormatterY,
}) => {
  const chartRef = useRef(null);
  const tooltipElRef = useRef<HTMLDivElement>(null);

  const chartData = useMemo(
    () => ({
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: JOURNEY_BEDROCK_BLACK,
          barPercentage: 0.5,
          borderRadius: { topLeft: 4, topRight: 4 },
        },
      ],
    }),
    []
  );

  const config: ChartConfiguration<'bar'> = useMemo(
    () => ({
      type: 'bar',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: false,
            text: legendLabel,
          },
          decimation: {},
          ...(renderTooltip && {
            tooltip: {
              intersect: false,
              enabled: false,
              position: 'nearest',
              external: (context) => renderTooltip(context, tooltipElRef),
            },
          }),
        },
        scales: {
          x: {
            ticks: {
              padding: 8,
              maxRotation: 0,
              color: JOURNEY_BEDROCK_BLACK,
              font: FONT_SPEC_PARAGRAPH,
            },
            grid: {
              drawTicks: false,
              drawOnChartArea: false,
            },
            display: true,
            title: {
              display: true,
              text: labelXAxis,
              color: JOURNEY_BEDROCK_DARK_GRAY,
              font: FONT_SPEC_PARAGRAPH,
            },
          },
          y: {
            ticks: {
              padding: 8,
              maxRotation: 0,
              color: JOURNEY_BEDROCK_BLACK,
              font: FONT_SPEC_PARAGRAPH,
              ...(tickLabelFormatterY && { callback: tickLabelFormatterY }),
            },
            grid: {
              drawTicks: false,
              drawBorder: false,
              color: JOURNEY_BEDROCK_GRAY_MEDIUM,
            },
            display: true,
            title: {
              display: true,
              text: labelYAxis,
              color: JOURNEY_BEDROCK_DARK_GRAY,
              font: FONT_SPEC_PARAGRAPH,
            },
          },
        },
      },
    }),
    [chartData]
  );

  useEffect(() => {
    if (chartRef.current) {
      new Chart(chartRef.current, { ...config });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return (
    <div className={canvasClasses || 'w-full h-auto relative'} style={canvasStyle}>
      <canvas ref={chartRef} className='w-full h-full' />
      <div ref={tooltipElRef} className='absolute top-0 left-0'></div>
    </div>
  );
};
